import React, { useEffect, useState, useRef } from 'react';
import { Avatar, MessageSimple, useChannelStateContext, useMessageContext } from 'stream-chat-react';
import './CustomMessageRenderer.css';
import CustomReactionSelectors from '../../src/components/CustomReactionSelectors/CustomReactionSelectors'

/**
 * Generates initials from a username
 * @param username  Full name of the user
 * @returns  Initials in uppercase
 */
const getNameInitials = (username: string): string => {
    let initials = '';
    if (username && username.trim() !== '') {
        const tempUserName = username.trim().split(' ');
        initials = tempUserName[0].charAt(0);
        if (tempUserName.length > 1) {
            initials += tempUserName[tempUserName.length - 1].charAt(0);
        }
    }
    return initials.toUpperCase();
};

/**
 *  CustomMessageRenderer Component
 * Renders a chat message with custom styling and features
 * FEATURES:
 * - Displays user avatar or initials
 * - Highlights @everyone mentions in public channels
 * - Shows message timestamp
 * - Integrates custom reaction selectors for public channels
 * @returns JSX.Element representing the custom message
 */
const CustomMessageRenderer: React.FC = () => {
    const { message } = useMessageContext();
    const [lastUserName, setLastUserName] = useState<string>('');
    const { channel } = useChannelStateContext(); // Get the current channel context
    const messageContainerRef = useRef<HTMLDivElement>(null);

    // Effect to update the last user name
    useEffect(() => {
        if (message && message.user) {
            setLastUserName(message.user.name || 'Unknown User');
        }
    }, [message]);

    // Effect to highlight @everyone text only
    useEffect(() => {
        if (channel?.type === "public_with_membership" && message?.text?.includes('@everyone') && messageContainerRef.current) {
            setTimeout(() => {
                // Find the message text element within this specific component instance
                const messageElement = messageContainerRef.current?.querySelector('.str-chat__message-text-inner');
                if (messageElement && messageElement.textContent?.includes('@everyone')) {
                    // Check if highlighting has already been applied to avoid nested spans
                    if (!messageElement.querySelector('.channel-mention-span')) {
                        const text = messageElement.innerHTML;
                        const highlightedText = text.replace(
                            /@everyone/gi,
                            '<span class="channel-mention-span">@everyone</span>'
                        );
                        messageElement.innerHTML = highlightedText;
                    }
                }
            }, 100);
        }
    }, [channel?.type, message?.text, message?.id]);

    // Early return if message is undefined or blocked
    if (!message) {
        console.error('Message is undefined or blocked');
        return null;
    }

    // Format the message timestamp
    const formatDate = (date: Date): string => {
        return date.toLocaleTimeString([], { hour: 'numeric', minute: 'numeric', hour12: true });
    };

    let content: JSX.Element;

    // Determine whether to show avatar or initials
    var imageURL = (message.user?.photourl) as any;
    if (imageURL === null || imageURL === undefined || imageURL === "") {
        var name = getNameInitials(lastUserName);
        content = <h4>{name}</h4>
    }
    else {
        // Show avatar if imageURL is available
        content = <Avatar image={imageURL} />;
    }


    return (
        <div className="custom-message left-aligned" ref={messageContainerRef}>

            {<div className={imageURL ? "" : "avatar-group channel_listing"}> {content}</div>}
            <div style={{width:'100%'}}>
                <div className="message-header">
                    <strong>{lastUserName}</strong>
                    <div className="message-time">
                        {formatDate(new Date(message.created_at!!))}
                    </div>
                </div> 
                <div className='message-chat-container'>
                    <div className={`${channel.type === "public_with_membership" && message.text?.includes('@everyone') ? 'highlight-channel-mention' : ''}`}>
                        <MessageSimple message={message} />
                    </div>
                    {!message.deleted_at && (channel.type === "public_without_membership" || channel.type === "public_without_membership_watchlive") && <CustomReactionSelectors />}
                </div>

            </div>
        </div>
    );
};

export default CustomMessageRenderer;
